import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Popover,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  Chip,
  TableSortLabel,
  InputAdornment
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { masterDataService } from '../services/masterData.service';

const ProductSupplierView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [orderBy, setOrderBy] = useState('sku');
  const [order, setOrder] = useState('asc');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');

  // Column configuration
  const [columns, setColumns] = useState([
    { id: 'sku', label: 'SKU', visible: true, sortable: false },
    { id: 'description', label: 'Description', visible: true, sortable: false },
    { id: 'product_id', label: 'Product ID', visible: false, sortable: false },
    { id: 'product_brand', label: 'Brand', visible: true, sortable: false },
    { id: 'product_manufacturer', label: 'Manufacturer', visible: true, sortable: false },
    { id: 'department', label: 'Department', visible: true, sortable: false },
    { id: 'category', label: 'Category', visible: true, sortable: true },
    { id: 'case_pack', label: 'Case Pack', visible: false, sortable: false },
    { id: 'unit_cost', label: 'Unit Cost', visible: true, sortable: false },
    { id: 'temperature_zone', label: 'Temp Zone', visible: false, sortable: false },
    { id: 'product_life_cycle_status', label: 'Life Cycle', visible: true, sortable: false },
    { id: 'supplier_info.supplier_id', label: 'Supplier ID', visible: false, sortable: false },
    { id: 'supplier_info.supplier_name', label: 'Supplier Name', visible: true, sortable: true },
    { id: 'supplier_info.minimal_order_qty', label: 'Min Order Qty', visible: true, sortable: false },
    { id: 'supplier_info.contract_price', label: 'Contract Price', visible: true, sortable: false },
    { id: 'supplier_info.current_price', label: 'Current Price', visible: true, sortable: false },
    { id: 'supplier_info.order_lead_time_days', label: 'Lead Time (Days)', visible: true, sortable: false },
    { id: 'price_difference', label: 'Price Diff', visible: true, sortable: false }
  ]);

  // Column visibility popover state
  const [anchorEl, setAnchorEl] = useState(null);

  // Get unique suppliers for filter dropdown
  const suppliers = useMemo(() => 
    [...new Set(products.map(p => p.supplier_info?.supplier_name).filter(Boolean))].sort(),
    [products]
  );

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await masterDataService.getProductSupplierData();
        if (result.status === 'success') {
          console.log('Fetched products count:', result.data.length);
          setProducts(result.data);
          setFilteredProducts(result.data);
          
          // Expand all categories initially
          const initialExpanded = {};
          result.data.forEach(product => {
            const category = product.category || 'Uncategorized';
            initialExpanded[category] = true;
          });
          setExpandedCategories(initialExpanded);
        } else {
          throw new Error(result.message || 'Failed to fetch product-supplier data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort function
  const getSortedData = (data) => {
    return [...data].sort((a, b) => {
      let aValue, bValue;

      if (orderBy.startsWith('supplier_info.')) {
        const field = orderBy.split('.')[1];
        aValue = a.supplier_info?.[field] || '';
        bValue = b.supplier_info?.[field] || '';
      } else {
        aValue = a[orderBy] || '';
        bValue = b[orderBy] || '';
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Apply search, supplier filter and sorting
  useEffect(() => {
    console.log('Starting filter with products count:', products.length);
    let filtered = [...products];
    
    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        String(p.sku || '').toLowerCase().includes(searchLower) ||
        String(p.description || '').toLowerCase().includes(searchLower)
      );
      console.log('After search filter count:', filtered.length);
    }

    // Apply supplier filter
    if (supplierFilter) {
      filtered = filtered.filter(p => 
        String(p.supplier_info?.supplier_name || '').trim().toLowerCase() === 
        String(supplierFilter).trim().toLowerCase()
      );
      console.log('After supplier filter count:', filtered.length);
    }
    
    // Apply sorting
    filtered = getSortedData(filtered);
    console.log('Final filtered count:', filtered.length);
    setFilteredProducts(filtered);
  }, [searchTerm, supplierFilter, products, order, orderBy]);

  // Group products by category
  const groupedProducts = useMemo(() => {
    console.log('Grouping products count:', filteredProducts.length);
    const grouped = filteredProducts.reduce((acc, product) => {
      const category = product.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(product);
      return acc;
    }, {});
    console.log('Categories found:', Object.keys(grouped).length);
    return grouped;
  }, [filteredProducts]);

  // Sort categories alphabetically
  const sortedCategories = useMemo(() => {
    return Object.keys(groupedProducts).sort();
  }, [groupedProducts]);

  // Toggle category expansion
  const toggleCategory = useCallback((category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  // Expand all categories
  const expandAll = useCallback(() => {
    const allExpanded = {};
    sortedCategories.forEach(category => {
      allExpanded[category] = true;
    });
    setExpandedCategories(allExpanded);
  }, [sortedCategories]);

  // Collapse all categories
  const collapseAll = useCallback(() => {
    setExpandedCategories({});
  }, []);

  // Column visibility handlers
  const handleColumnVisibilityClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColumnVisibilityClose = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = (columnId) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  };

  const formatCurrency = (value) => {
    return value ? `$${Number(value).toFixed(2)}` : '';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Product Supplier View
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search SKU or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Supplier</InputLabel>
            <Select
              value={supplierFilter}
              label="Supplier"
              onChange={(e) => setSupplierFilter(e.target.value)}
            >
              <MenuItem value="">All Suppliers</MenuItem>
              {suppliers.map(supplier => (
                <MenuItem key={supplier} value={supplier}>{supplier}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={handleColumnVisibilityClick}>
            <ViewColumnIcon />
          </IconButton>
          <Button
            size="small"
            onClick={expandAll}
            variant="outlined"
            startIcon={<ExpandMoreIcon />}
          >
            Expand All
          </Button>
          <Button
            size="small"
            onClick={collapseAll}
            variant="outlined"
            startIcon={<ExpandLessIcon />}
          >
            Collapse All
          </Button>
        </Box>
      </Box>

      {/* Column Visibility Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleColumnVisibilityClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Visible Columns
          </Typography>
          <FormGroup>
            {columns.map((column) => (
              <FormControlLabel
                key={column.id}
                control={
                  <Checkbox
                    checked={column.visible}
                    onChange={() => handleColumnToggle(column.id)}
                  />
                }
                label={column.label}
              />
            ))}
          </FormGroup>
          <Button 
            variant="contained" 
            onClick={handleColumnVisibilityClose}
            sx={{ mt: 2 }}
          >
            Close
          </Button>
        </Box>
      </Popover>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.filter(col => col.visible).map((column) => (
                <TableCell 
                  key={column.id}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCategories.map((category) => (
              <React.Fragment key={category}>
                <TableRow 
                  sx={{ 
                    backgroundColor: 'grey.100',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'grey.200',
                    }
                  }}
                  onClick={() => toggleCategory(category)}
                >
                  <TableCell colSpan={columns.filter(col => col.visible).length}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {expandedCategories[category] ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {category}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({groupedProducts[category].length} items)
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
                {expandedCategories[category] && groupedProducts[category].map((product) => (
                  <TableRow key={`${product.product_id}-${product.supplier_info?.supplier_id}`}>
                    {columns.filter(col => col.visible).map((column) => {
                      let value;
                      if (column.id.startsWith('supplier_info.')) {
                        const field = column.id.split('.')[1];
                        value = product.supplier_info?.[field] ?? '';
                      } else {
                        value = product[column.id] ?? '';
                      }

                      if (column.id === 'price_difference') {
                        return (
                          <TableCell 
                            key={`${product.product_id}-${product.supplier_info?.supplier_id}-${column.id}`}
                            sx={{ 
                              color: value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'inherit'
                            }}
                          >
                            {formatCurrency(value)}
                          </TableCell>
                        );
                      }

                      if (column.id === 'product_life_cycle_status') {
                        return (
                          <TableCell key={`${product.product_id}-${product.supplier_info?.supplier_id}-${column.id}`}>
                            <Chip 
                              label={value || 'UNKNOWN'}
                              color={value === 'ACTIVE' ? 'success' : 'default'}
                            />
                          </TableCell>
                        );
                      }

                      if (['unit_cost', 'supplier_info.contract_price', 'supplier_info.current_price'].includes(column.id)) {
                        return (
                          <TableCell key={`${product.product_id}-${product.supplier_info?.supplier_id}-${column.id}`}>
                            {formatCurrency(value)}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={`${product.product_id}-${product.supplier_info?.supplier_id}-${column.id}`}>
                          {value || '-'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ProductSupplierView; 